import { Typography } from '@euler/app/components/typography';
import {
  ChangeLogEntry,
  releaseNotes,
} from '@euler/app/flows/settings/functions/releaseNotes';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { nanoid } from 'nanoid/non-secure';
import { memo, useMemo } from 'react';
import {
  Platform,
  SectionList,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// type ChangeType = ChangeLogEntry['type'];

type Item =
  | { type: 'group-label'; label: string; id: string }
  | { type: 'entry'; entry: ChangeLogEntry; id: string };

const Cell = memo(
  ({
    item,
    style,
    groupStyle,
  }: {
    item: Item;
    style?: StyleProp<ViewStyle>;
    groupStyle?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    if (item.type === 'group-label') {
      return (
        <Typography.Label
          regular
          css={`
            margin-top: ${rpx(10)}px;
            margin-bottom: ${rpx(5)}px;
          `}
          style={groupStyle}
        >
          {item.label}
        </Typography.Label>
      );
    }
    return (
      <HStack justifyContent="flex-start" alignItems="flex-start" style={style}>
        <Typography.Label
          light
          size={rpx(14)}
          lineHeight={rpx(24)}
        >{`• `}</Typography.Label>
        <Typography.Label
          light
          size={rpx(14)}
          lineHeight={rpx(24)}
          css={`
            margin-right: ${rpx(15)}px;
          `}
        >{`${item.entry.platform ? `[${item.entry.platform}] ` : ''}${
          item.entry.title
        }`}</Typography.Label>
      </HStack>
    );
  },
);

type Section = {
  title: string;
  data: Item[];
};

export const ReleaseNotesScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();

    const sections = useMemo<Section[]>(
      () =>
        releaseNotes.map(changeSet => {
          const section: Section = {
            title: changeSet.date,
            data: [],
          };

          const changes = changeSet.changes.filter(
            x => !x.platform || x.platform === Platform.OS,
          );

          const features = changes.filter(x => x.type === 'feat');
          const fixes = changes.filter(x => x.type === 'fix');
          const perfs = changes.filter(x => x.type === 'perf');

          const addGroup = (label: string, items: ChangeLogEntry[]) => {
            if (items.length > 0) {
              section.data.push({ type: 'group-label', label, id: nanoid() });
              section.data.push(
                ...items.map<Item>(entry => ({
                  type: 'entry',
                  entry,
                  id: nanoid(),
                })),
              );
            }
          };

          addGroup('功能及优化', features);
          addGroup('问题修复', fixes);
          addGroup('性能优化', perfs);

          return section;
        }),
      [],
    );

    return (
      <SectionList
        sections={sections}
        stickySectionHeadersEnabled={false}
        contentContainerStyle={{
          paddingLeft: rpx(15),
          paddingRight: rpx(15),
          paddingTop: rpx(15),
          paddingBottom: insets.bottom + rpx(10),
        }}
        initialNumToRender={20}
        keyExtractor={item => item.id}
        renderSectionHeader={({ section }) => (
          <Typography.Label medium size={rpx(18)}>
            {section.title}
          </Typography.Label>
        )}
        renderItem={({ item }) => <Cell item={item} />}
        renderSectionFooter={() => (
          <View
            css={`
              height: ${rpx(24)}px;
            `}
          />
        )}
        css={`
          flex: 1;
        `}
      />
    );
  },
  {
    title: '更新日志',
  },
);
